import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'
import { Languages } from '@wicadu/langscope'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Cómo crear una venta manual',
    seo_description:
      'Aprende a crear una venta manual. Agrega productos al carrito, ingresa los datos de facturación y envío del cliente, y completa el proceso de pago de forma sencilla.',
    seo_keywords:
      'cómo crear un pedido manual en wicadu, cómo crear una venta manual en wicadu, pedidos manuales wicadu, crear pedido manual, ventas manuales wicadu, agregar productos a pedido manual, gestión de inventario, configurar opciones de pago wicadu, cambiar estado pedido a preparado, cómo adjuntar documentos a un pedido, cancelar pedido manual wicadu, marcar pedido como entregado wicadu, pedidos manuales por teléfono,  asistente de wicadu pedidos',
    page_id: PagesId.CreateManualUserOrders,
    breadcrumbs: {
      home: 'Inicio',
      orders_home: 'Pedidos',
      create: 'Cómo crear una venta manual',
    },
    article: {
      title: 'Cómo crear una venta manual',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para configurar <em>opciones de pago</em>, <em>métodos de entrega</em> y <em>gestión de la tienda</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST}>asistente de wicadu</a>.`,
        `<strong>Vendedores</strong> con permisos para gestionar <em>inventario</em> y <em>crear ventas manuales</em>.`,
      ],
      about_create_orders: {
        title: 'Acerca de crear un pedido manual',
        content_type: ContentType.Paragraph,
        content: [
          `Los <strong>pedidos manuales</strong> son una solución clave para registrar pedidos que se realizan fuera de tu tienda en línea. Ya sea que recibas pedidos por <strong>teléfono</strong>, <strong>WhatsApp</strong>, <strong>Instagram</strong> u otros canales de comunicación, esta función te permite mantener tu inventario actualizado y asegurar que todos los pedidos se registren correctamente.`,
          `Al crear un pedido manual, puedes agregar productos al carrito, ingresar los detalles de facturación y envío, y completar el proceso de pago de forma sencilla. Además, puedes establecer el estado del pedido, ya sea como <strong>Confirmado</strong> o <strong>Pendiente</strong>, según el <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.Spanish)}>método de pago</a> utilizado, como <strong>efectivo</strong> o <strong>Transferencia bancaria</strong>. Este proceso es ideal para gestionar ventas directas y mejorar la experiencia de tus clientes.`,
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `Antes de crear una <strong>venta manual</strong>, asegúrate de que los productos estén correctamente registrados en tu sistema, con precios asignados y disponibilidad en el inventario. Para hacerlo, sigue estos pasos:`,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<a href=${getUrlByPageId(PagesId.CreateItems, Languages.Spanish)} title="Guía para crear un producto en wicadu">Cómo crear un producto</a>.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.Spanish)} title="Guía para agregar compras">Cómo agregar compras</a>`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `Esto es fundamental para poder gestionar las ventas de manera efectiva y garantizar que todo el proceso se realice sin inconvenientes.`,
            ],
          },
        ],
      },
      how_to_create_orders: {
        title: 'Pasos para crear un pedido manual',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Ventas: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Accede a la funcionalidad de <em>Ventas</em> en el asistente de <strong>wicadu</strong>.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Crear un nuevo pedido: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en el botón <b>+</b> ubicado en la esquina superior derecha de la página.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Seleccionar los productos: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Si aún no has agregado productos, serás redirigido automáticamente a la lista de productos. Si ya tienes productos agregados, haz clic en <strong>Agregar producto</strong> si deseas incluir mas productos.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  // '<strong>Nota:</strong> Los productos sin disponibilidad o sin precio asignado mostrarán una alerta. Más detalles en este <a href="#">artículo</a>.',
                  '<strong>Nota:</strong> Los productos sin disponibilidad o sin precio asignado mostrarán una alerta.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Revisar y crear el pedido: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en <strong>Continuar</strong> para ver el resumen y luego en <strong>Crear pedido</strong>. Esto cambiará el estado del pedido a <strong>Preparado</strong>.',
                ],
              },
            ],
          },
        ],
      },
      next_steps_on_orders: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Una vez que el pedido esté en estado "Preparado", puedes proceder con las siguientes acciones:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Adjuntar Documentos Opcionales: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Adjunta documentos como factura, recibo, etiqueta de envío, prueba de entrega y más. Consulta
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.Spanish)} title="Guía para adjuntar documentos a un pedido">Cómo adjuntar documentos a un pedido</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Cancelar un Pedido: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si cometiste un error o el cliente decide no continuar con el pedido, aprende cómo cancelarlo en 
                      <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.Spanish)} title="Guía para cancelar un pedido">Cómo cancelar un pedido</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Agregar Pagos: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Registra los pagos realizados por el cliente en
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.Spanish)} title="Guía para agregar pagos a un pedido">Cómo agregar pagos a un pedido</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Entregar el Pedido: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // 'Marca el pedido como entregado con <a href="https://help.wicadu.com/marcar-entregado" title="Guía para marcar un pedido como entregado">Cómo marcar un pedido como entregado</a>.',
                      'Marca el pedido como entregado.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Corregir errores en el pedido:']
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si cometiste un error al crear un pedido, puedes procesar una <strong>devolución</strong>, <strong>cambio</strong> o <strong>reembolso</strong>. Aprende cómo hacerlo aquí:
                      <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.Spanish)} title="Guia para procesar devoluciones, cambios y reembolsos en wicadu">Cómo procesar una devolución, cambio o reembolso</a>`
                    ]
                  }
                ]
              }
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>No puedo agregar un producto:</strong> Verifica que esté registrado y con stock.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El pedido no cambia a Preparado:</strong> Asegúrate de que todos los productos estén configurados correctamente.',
            ],
          },
        ],
      },
    },
  },
  en: {
    seo_title: 'How to add a Manual Sale',
    seo_description:
      'Learn how to add manual sales orders in wicadu. Select products, input billing and shipping details, and complete the checkout process efficiently.',
    seo_keywords: '',
    page_id: PagesId.CreateManualUserOrders,
    breadcrumbs: {
      home: 'Home',
      orders_home: 'Orders',
    },
    article: {
      title: 'How to add a Manual Sale',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to configure <em>payment options</em>, <em>delivery methods</em>, and <em>store operations</em> within <a href=${process.env.STORES_WEBAPP_HOST}>wicadu Assistant</a>.`,
        '<strong>Sellers</strong> with permissions to manage <em>inventory</em> and <em>manual sales</em>.',
      ],
      about_create_orders: {
        title: 'About creating a Manual Order',
        content_type: ContentType.Paragraph,
        content: [
          '<strong>Manual orders</strong> are a key solution for recording sales that happen outside your online store. Whether you receive orders via <strong>phone</strong>, <strong>WhatsApp</strong>, <strong>Instagram</strong>, or other communication channels, this feature helps you keep your inventory updated and ensures all orders are accurately tracked.',
          `When <strong>creating a manual order</strong>, you can add products to the cart, enter billing and shipping details, and complete the payment process easily. Additionally, you can set the order status to either <strong>Confirmed</strong> or <strong>Pending</strong>, depending on the <a href=${getUrlByPageId(
            PagesId.OrdersPaymentMethods,
            Languages.English
          )}>payment method</a> used, such as <strong>cash</strong> or <strong>bank transfer</strong>. This process is perfect for <strong>managing direct sales</strong> and enhancing the customer experience`,
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `Before creating a <strong>manual sale</strong>, ensure that all products are correctly registered in your system, with assigned prices and available stock. To do this, follow these steps:`,
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<a href=${getUrlByPageId(PagesId.CreateItems, Languages.English)} title="How to create a product guide">How to create a product</a>.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.English)} title="How to add purchases guide">How to add purchases</a>`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'This is essential for efficiently managing sales operations and ensuring a seamless ordering process.',
            ],
          },
        ],
      },
      how_to_create_orders: {
        title: 'Steps to Create a Manual Sale',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to <strong>Sales</strong>: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Access the <em>Sales</em> functionality in the <strong>wicadu</strong> assistant.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Create a new order: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click the <b>+</b> button located in the upper right corner of the page.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select the products: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `If you haven't added products yet, you will be automatically directed to the product list. If you have already added products, click <strong>Add Product</strong> to include more items.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  // `<strong>Note:</strong> Products that are out of stock or missing a price will trigger an alert. See more details in this <a href="#">article</a>.`,
                  `<strong>Note:</strong> Products that are out of stock or missing a price will trigger an alert.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Review and create the order: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click <strong>Continue</strong> to review the <strong>order summary</strong>, then select <strong>Create Order</strong> to complete the process. This will update the order status to <strong>Prepared</strong>.',
                ],
              },
            ],
          },
        ],
      },
      next_steps_on_orders: {
        title: 'Next Steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Once the order status is set to "Prepared", you can proceed with the following actions:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Attach Optional Documents: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Upload supporting documents such as invoices, receipts, shipping labels, proof of delivery, and more. See the guide:
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.English)} title="How to Attach Documents to an Order Guide">How to Attach Documents to an Order</a>`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Cancel an Order: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `If an error was made or the customer decides not to proceed, learn how to cancel an order in: 
                      <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.English)} title="How to Cancel an Order Guide">How to Cancel an Order</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Add Payments: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Record customer payments following this guide:
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.English)} title="How to Add Payments to an Order Guide">How to Add Payments to an Order</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Mark the Order as Delivered: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // `Update the order status to "Delivered" by following the steps in: <a href="https://help.wicadu.com/marcar-entregado" title="How to Mark an Order as Delivered Guide">How to Mark an Order as Delivered</a>.`,
                      `Update the order status to "Delivered".`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Correct mistakes in an order:']
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `If you made a mistake creating an order, you can process a <strong>return</strong>, <strong>exchange</strong>, or <strong>refund</strong>. Learn how to do it here:
                      <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.English)} title="Process Returns, Exchanges, and Refunds in wicadu guide">How to process a return, exchange, or refund</a>`
                    ]
                  }
                ]
              }
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>I can't add a product:</strong> Ensure that the product is correctly registered and in stock.`,
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>The order isn't changing to 'Prepared':</strong> Make sure that all products are properly configured.`,
            ],
          },
        ],
      },
    },
  },
  fr: {
    seo_title: 'Créer une vente manuelle',
    seo_description:
      'Découvrez comment enregistrer une vente manuelle dans wicadu, ajouter des produits et compléter les informations de paiement et de livraison.',
    seo_keywords:
      'vente manuelle, commande manuelle, ventes wicadu, caisse, paiement commande, inventaire',
    page_id: PagesId.CreateManualUserOrders,
    breadcrumbs: {
      home: 'Accueil',
      orders_home: 'Commandes',
      create: 'Créer une vente manuelle',
    },
    article: {
      title: 'Créer une vente manuelle',
      intended: [
        `<strong>Propriétaires</strong>, <strong>administrateurs</strong> et équipes de vente qui créent des <em>commandes manuelles</em> dans le <a href=${process.env.STORES_WEBAPP_HOST}>tableau de bord wicadu Stores</a>.`,
        '<strong>Vendeurs</strong> autorisés à enregistrer des ventes directes et à suivre les paiements des commandes.',
      ],
      about_create_orders: {
        title: 'À propos des ventes manuelles',
        content_type: ContentType.Paragraph,
        content: [
          'Les <strong>ventes manuelles</strong> sont une solution essentielle pour enregistrer les commandes réalisées en dehors de votre boutique en ligne. Que vous receviez des ventes par <strong>téléphone</strong>, <strong>WhatsApp</strong>, <strong>Instagram</strong>, via des applications de messagerie ou par d’autres canaux directs, cette fonctionnalité vous aide à garder votre <strong>inventaire</strong> à jour et à centraliser correctement chaque <strong>commande manuelle</strong> dans <strong>wicadu</strong>.',
          `Lors de la création d’une <strong>vente manuelle</strong>, vous pouvez ajouter des <strong>produits</strong> au panier, renseigner les données de <strong>facturation</strong> et de <strong>livraison</strong>, puis finaliser le processus de paiement. Vous pouvez aussi définir le statut de la commande selon le <a href=${getUrlByPageId(PagesId.OrdersPaymentMethods, Languages.French)} title="Modes de paiement pour les commandes dans wicadu">mode de paiement</a> utilisé, par exemple <strong>espèces</strong>, <strong>carte</strong> ou <strong>virement bancaire</strong>. Ce flux est idéal pour mieux gérer les <strong>ventes directes</strong>, améliorer l’<strong>expérience client</strong> et assurer un meilleur suivi des <strong>paiements</strong> dans votre boutique.`,
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Avant de créer une <strong>vente manuelle</strong>, assurez-vous que les produits sont correctement enregistrés dans votre système, avec un <strong>prix</strong> défini et une <strong>disponibilité en stock</strong> suffisante. Pour préparer correctement votre catalogue et votre inventaire, suivez d’abord ces guides :',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<a href=${getUrlByPageId(PagesId.CreateItems, Languages.French)} title="Créer un produit dans wicadu">Comment créer un produit</a>.`,
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `<a href=${getUrlByPageId(PagesId.CreateManualStoreOrders, Languages.French)} title="Créer un achat manuel dans wicadu">Comment ajouter des achats</a>.`,
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Cette préparation est essentielle pour gérer les <strong>ventes</strong> efficacement et garantir un processus de commande fluide, précis et sans erreur.',
            ],
          },
        ],
      },
      how_to_create_orders: {
        title: 'Étapes pour créer une vente manuelle',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Accéder au module des commandes : '] },
              { content_type: ContentType.Paragraph, content: ['Ouvrez la section <em>Commandes</em> dans wicadu.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Créer une nouvelle commande : '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Cliquez sur le bouton <b>+</b> en haut à droite de la page.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Sélectionner les produits : '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Si aucun produit n’a encore été ajouté, vous serez redirigé vers la liste des produits. Si des produits sont déjà disponibles, cliquez sur <strong>Ajouter un produit</strong> pour en inclure davantage.',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                class: 'msg-alert',
                content: [
                  '<strong>Remarque :</strong> les produits sans stock disponible ou sans prix défini afficheront une alerte.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Vérifier puis créer la commande : '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Cliquez sur <strong>Continuer</strong> pour vérifier le résumé de la commande, puis sur <strong>Créer la commande</strong> pour terminer. Le statut passera alors à <strong>Préparée</strong>.',
                ],
              },
            ],
          },

        ],
      },
      next_steps_on_orders: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: ['Une fois que le statut de la commande est défini sur <em>"Préparée"</em>, vous pouvez poursuivre avec les actions suivantes :'],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Ajouter des documents :'] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Ajoutez des documents utiles comme une facture, un reçu, une étiquette d’expédition ou une preuve de livraison. Consultez :
                      <a href=${getUrlByPageId(PagesId.ManagingDocsForOrders, Languages.French)} title="Comment joindre des documents à une commande dans wicadu">Comment joindre des documents à une commande</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Enregistrer les paiements : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Suivez les paiements liés à cette vente en vous aidant de ce guide :
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.French)} title="Comment ajouter des paiements à une commande dans wicadu">Comment ajouter des paiements à une commande</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Annuler une commande : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Si une erreur a été commise ou si le client ne souhaite plus poursuivre, découvrez comment annuler la commande ici :
                      <a href=${getUrlByPageId(PagesId.HowToCancelOrders, Languages.French)} title="Comment annuler une commande dans wicadu">Comment annuler une commande</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Marquer la commande comme livrée : '] },
                  { content_type: ContentType.Paragraph, content: ['Lorsque la vente est finalisée, mettez la commande à jour comme livrée.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Corriger les erreurs de commande : '] },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `En cas d’erreur lors de la création de la commande, vous pouvez traiter un <strong>retour</strong>, un <strong>échange</strong> ou un <strong>remboursement</strong>. Consultez :
                      <a href=${getUrlByPageId(PagesId.OrdersReturns, Languages.French)} title="Comment traiter un retour, un échange ou un remboursement dans wicadu">Comment traiter un retour, un échange ou un remboursement</a>.`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Impossible d’ajouter un produit</strong> : vérifiez qu’il est bien créé et disponible.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>La commande reste incomplète</strong> : contrôlez les données obligatoires avant de confirmer.'] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
